import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { accessSessionTokenKeyName, loggedInAdminAccessToken } from 'src/utils/constants'
import { appendColumnFiltersToParams } from '../listing-product-table-lite/buildColumnFilterParams'
import {
  ProductVariantsResponse,
  ColumnFilter,
  SortState,
  SelectedColumn,
  ProductVariant,
  CatalogColumn,
} from './types'

interface UseTableDataOptions {
  channelId: string | number
  page: number
  pageSize: number
  globalSearch: string
  columnFilters: ColumnFilter[]
  sortState: SortState | null
  selectedColumns: SelectedColumn[]
  /** When false, skips network fetch (eg:- parent hides the lite table on another tab). */
  enabled?: boolean
}

const ALWAYS_REQUESTED_SUPPORT_COLUMNS = [
  'id',
  'variant_parent',
  'parent',
  'parent_details',
  'bundle',
  'platform_product',
  'platform_product_parent',
  'sync_status',
  'sync_message',
  'primary_sync_status',
  'primary_sync_message',
  'accounting_sync_status',
  'accounting_sync_message',
  'shipping_sync_status',
  'shipping_sync_message',
  'erp_sync_status',
  'erp_sync_message',
  'erp_secondary_sync_status',
  'erp_secondary_sync_message',
]

const mapFieldForApi = (field?: string) => {
  if (!field) return ''
  if (field === 'product_name') return 'name'
  if (field === 'link_actions') return 'link_action'
  return field
}

const normalizeParentType = (type?: string) => {
  const raw = (type || '').trim().toLowerCase().replace(/\s+/g, '_')
  if (!raw) return ''
  if (raw === 'primary') return 'primary'
  if (raw === 'accounting') return 'accounting'
  if (raw === 'shipping') return 'shipping'
  if (raw === 'erp') return 'erp'
  if (raw === 'erp_secondary' || raw === 'erp-secondary') return 'erp_secondary'
  return raw
}

const normalizeSyncState = (value?: string | null) => {
  if (!value) return value
  const upper = String(value).trim().toUpperCase()
  return upper === 'LINKED' ? 'SYNCED' : upper
}

const normalizeRowForLegacyComponents = (row: ProductVariant): ProductVariant => {
  const normalized: ProductVariant = {
    ...row,
    parent: row?.parent ?? row?.variant_parent ?? null,
  }

  if (normalized.platform_product && typeof normalized.platform_product === 'object') {
    normalized.platform_product = {
      ...normalized.platform_product,
      parent:
        normalized.platform_product?.parent ??
        normalized.platform_product_parent ??
        normalized?.parent ??
        null,
    }
  }

  normalized.sync_status = normalizeSyncState(normalized.sync_status)
  normalized.primary_sync_status = normalizeSyncState(normalized.primary_sync_status)
  normalized.accounting_sync_status = normalizeSyncState(normalized.accounting_sync_status)
  normalized.shipping_sync_status = normalizeSyncState(normalized.shipping_sync_status)
  normalized.erp_sync_status = normalizeSyncState(normalized.erp_sync_status)
  normalized.erp_secondary_sync_status = normalizeSyncState(normalized.erp_secondary_sync_status)

  if (!normalized.sync_status && normalized.primary_sync_status) {
    normalized.sync_status = normalizeSyncState(normalized.primary_sync_status)
  }
  if (!normalized.sync_message && normalized.primary_sync_message) {
    normalized.sync_message = normalized.primary_sync_message
  }

  const parentDetails = Array.isArray(row?.parent_details) ? row.parent_details : []
  parentDetails.forEach((item: any) => {
    const type = normalizeParentType(item?.type)
    if (!type) return

    const baseKey = type === 'primary' ? '' : `${type}_`
    const parentKey = type === 'primary' ? 'parent' : `${baseKey}parent`
    const statusKey = type === 'primary' ? 'sync_status' : `${baseKey}sync_status`
    const messageKey = type === 'primary' ? 'sync_message' : `${baseKey}sync_message`

    if (normalized[parentKey] == null && item?.id != null) {
      normalized[parentKey] = item.id
    }

    if (!normalized[statusKey]) {
      const perPlatformStatusPattern = new RegExp(`^${baseKey}sync_status_(\\d+)$`)
      let resolvedStatus: string | null = null

      for (const key of Object.keys(item)) {
        if (perPlatformStatusPattern.test(key) && item[key]) {
          resolvedStatus = item[key]
          if (!normalized[key]) {
            normalized[key] = normalizeSyncState(item[key])
          }
          break
        }
      }

      resolvedStatus =
        resolvedStatus ??
        item?.[`${baseKey}sync_status`] ??
        item?.sync_status ??
        null

      normalized[statusKey] = normalizeSyncState(resolvedStatus)
    }

    if (!normalized[messageKey]) {
      const perPlatformMessagePattern = new RegExp(`^${baseKey}sync_message_(\\d+)$`)
      let resolvedMessage: string | null = null

      for (const key of Object.keys(item)) {
        if (perPlatformMessagePattern.test(key) && item[key]) {
          resolvedMessage = item[key]
          break
        }
      }

      normalized[messageKey] =
        resolvedMessage ??
        item?.[`${baseKey}sync_message`] ??
        item?.sync_message ??
        null
    }
  })

  return normalized
}

export const useTableData = ({
  channelId,
  page,
  pageSize,
  globalSearch,
  columnFilters,
  sortState,
  selectedColumns,
  enabled = true,
}: UseTableDataOptions) => {
  const [data, setData] = useState<ProductVariant[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [responseColumns, setResponseColumns] = useState<CatalogColumn[]>([])
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Build query parameters
      const params = new URLSearchParams()
      params.append('page', String(page))
      params.append('limit', String(pageSize))

      if (globalSearch) {
        params.append('search_string', globalSearch)
      }

      // Add sort
      if (sortState) {
        params.append('sort_by', mapFieldForApi(sortState.field))
        params.append('sort_order', sortState.direction.toUpperCase())
      }

      appendColumnFiltersToParams(params, columnFilters, mapFieldForApi)

      // Add selected columns (exclude synthetic UI-only fields) + required support columns, deduped.
      // If no columns are selected yet, don't send columns[] so backend default visibility can drive first render.
      const selectedApiFields = selectedColumns
        .map((col) => mapFieldForApi(col.field))
        .filter((field) => field && field !== 'linking_status' && field !== 'link_actions' && field !== 'link_action')
      if (selectedApiFields.length > 0) {
        const uniqueColumns = Array.from(new Set([...selectedApiFields, ...ALWAYS_REQUESTED_SUPPORT_COLUMNS]))
        uniqueColumns.forEach((field) => {
          params.append('columns[]', field)
        }) 
      } 

      const token = localStorage.getItem(accessSessionTokenKeyName)
      const adminToken = localStorage.getItem(loggedInAdminAccessToken)
      const response = await axios.get<ProductVariantsResponse>(
        `${process.env.REACT_APP_API_URL}/api/platform-product-variants-ag-grid/${channelId}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'xadmin': adminToken || null,
          }, 
        } 
      ) 

      const payload: any = response.data?.data || response.data
      const rows =
        payload?.results ||
        payload?.rows ||
        payload?.data?.results ||
        payload?.data?.rows ||
        payload?.items ||
        payload?.records ||
        []
      const count = payload?.count ?? payload?.total ?? payload?.meta?.total ?? 0
      const columns = payload?.columns || payload?.data?.columns || []

      const normalizedRows = (Array.isArray(rows) ? rows : []).map(normalizeRowForLegacyComponents)
      setData(normalizedRows)
      setTotal(Number(count) || 0)
      setResponseColumns(Array.isArray(columns) ? columns : [])
      
      // Calculate total pages
      const calculatedTotalPages = pageSize > 0 ? Math.ceil((Number(count) || 0) / pageSize) : 0
      setTotalPages(calculatedTotalPages)
    } catch (err: any) {
      console.error('Error fetching table data:', err)
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch data'
      if (err.response?.status === 403) {
        setError(`Access denied: ${errorMessage}. Please ensure you have admin access to this platform.`)
      } else {
        setError(errorMessage)
      } 
      setData([]) 
      setTotal(0)
      setTotalPages(0)
      setResponseColumns([])
    } finally { 
      setLoading(false) 
      setHasLoadedOnce(true)
    } 
  }, [channelId, page, pageSize, globalSearch, columnFilters, sortState, selectedColumns])

  useEffect(() => {
    if (!enabled) return
    fetchData()
  }, [fetchData, enabled])

  return {
    data,
    total,
    totalPages,
    loading,
    error,
    responseColumns,
    hasLoadedOnce,
    refetch: fetchData,
  } 
} 
